import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import GoogleIcon from "@mui/icons-material/Google";
import { toast } from "sonner";

import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable";
import { useAuth } from "@/hooks/useAuth";

export const Route = createFileRoute("/signup")({
  component: SignupPage,
  head: () => ({
    meta: [
      { title: "Create account — JobYoda" },
      { name: "description", content: "Start matching yourself to roles, generating tailored materials, and tracking your pipeline with JobYoda." },
      { property: "og:title", content: "Create account — JobYoda" },
      { property: "og:description", content: "Sign up free and score yourself against any job description in minutes." },
      { property: "og:type", content: "website" },
    ],
  }),
});

function SignupPage() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!loading && user) navigate({ to: "/pricing" });
  }, [user, loading, navigate]);

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    const { data, error: err } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/pricing`,
        data: { name },
      },
    });
    setSubmitting(false);
    if (err) {
      toast.error(err.message);
      return;
    }
    try { localStorage.setItem("plan_choice_pending", "1"); } catch { /* ignore */ }
    if (data.session) {
      navigate({ to: "/pricing" });
    } else {
      toast.success("Check your email to confirm your account.");
    }
  };

  const handleGoogle = async () => {
    try { localStorage.setItem("plan_choice_pending", "1"); } catch { /* ignore */ }
    const result = await lovable.auth.signInWithOAuth("google", {
      redirect_uri: `${window.location.origin}/pricing`,
    });
    if (result.error) toast.error(result.error.message);
  };

  return (
    <Box sx={{ minHeight: "100vh", display: "grid", placeItems: "center", bgcolor: "background.default", p: 2 }}>
      <Container component="main" maxWidth="xs" disableGutters>
        <Card>
          <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
            <Typography variant="h5" sx={{ mb: 0.5, fontWeight: 700 }}>
              Create your account
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Start scoring yourself against jobs in minutes.
            </Typography>

            <Button
              fullWidth
              variant="contained"
              onClick={() => {
                try { localStorage.setItem("plan_choice_pending", "1"); } catch { /* ignore */ }
                window.location.href = "/api/public/auth/dollyland/start?next=/pricing";
              }}
              sx={{ mb: 1.5 }}
            >
              Sign up with Dollyland
            </Button>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<GoogleIcon />}
              onClick={handleGoogle}
              sx={{ mb: 2 }}
            >
              Continue with Google
            </Button>
            <Divider sx={{ my: 2 }}>or</Divider>

            <Stack component="form" spacing={2} onSubmit={handleSignup}>
              <TextField
                label="Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                fullWidth
                autoComplete="name"
              />
              <TextField
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                fullWidth
              />
              <TextField
                label="Password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="new-password"
                fullWidth
                helperText="At least 6 characters."
              />
              <Button type="submit" variant="contained" size="large" disabled={submitting}>
                {submitting ? "Creating…" : "Create account"}
              </Button>
            </Stack>

            <Typography variant="body2" color="text.secondary" sx={{ mt: 3, textAlign: "center" }}>
              Already have an account?{" "}
              <Link to="/login" style={{ color: "inherit", fontWeight: 600 }}>
                Sign in
              </Link>
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: "block", textAlign: "center" }}>
              By creating an account you agree to our{" "}
              <Link to="/terms" style={{ color: "inherit", fontWeight: 600 }}>Terms</Link>
              {" · "}
              <Link to="/support" style={{ color: "inherit" }}>Support</Link>
            </Typography>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
}
